import { useEffect, useRef, useState } from 'react';
import { areasOf, clamp, defaultShadingSettings, newTemplateId, titleCaseId, type InkLook, type MockupTemplate, type PrintArea } from '@eip/engine';
import { useStore } from '../state/store';
import { deleteUserTemplate, templateStoreKind } from '../app/session';
import { OFFSET_IN_MAX, OFFSET_IN_MIN, ROTATION_MAX, ROTATION_MIN, WIDTH_IN_MAX, WIDTH_IN_MIN } from '../state/mockup';
import { candidatesFor, groupByStyle, SESSION_ONLY_NOTICE, type TemplateSource } from '../lib/templates';
import {
  artSizeLine, defaultExtraPlacement, extraDesignOptions, extraPrintFor, garmentForColors, LOOK_LABELS, placementPatch, retainedPrints,
  printFits, setExtraPrint, templateChange, templateOptions, withoutPrint, type ExtraPrintPatch,
} from '../lib/mockup-ui';
import { PLACEMENT_PRESETS, alignPlacement, applyPreset, currentAnchor, matchPreset, type PresetId } from '../lib/placement-presets';
import { Select, Slider, Toggle, commitDraft, nextDraft } from './controls';
import { undoGroup } from '../state/undo';
import { AlignGrid } from './AlignGrid';
import { BackdropControls } from './BackdropControls';
import { ColorSwatches } from './ColorSwatches';
import { TemplateEditor } from './TemplateEditor';
import type { MockupFormat } from '../worker/protocol';
import { Section } from './Section';

/**
 * Where a mockup panel entry belongs: this document, in mockup mode — the only mode
 * whose subscription turns a restored job or colourway into a render. The store's own
 * job writes carry the same scope (`store.ts`'s `JOB_SCOPE`); this is the group's,
 * since a group is what reaches the stack.
 */
const mockupScope = (docId: string) => ({ docId, mode: 'mockup' });

// Mirrors `sanitizeMockupDefaults`'s (unexported) sheet-tile-width bounds.
const TILE_MIN = 200, TILE_MAX = 2000;
const FORMAT_OPTIONS = [{ value: 'png', label: 'PNG' }, { value: 'jpeg', label: 'JPEG' }];

/**
 * The right-hand panel for mockup mode: template, garment colours, front/back
 * design, placement, how it is printed, realism and export settings, plus a status
 * line for the last (or in-flight) render. Mounted instead of `PipelinePanel`
 * when `appMode === 'mockup'`, keyed by the active document the same way
 * `SepPanel` is — this panel's own local state (the offset drafts below)
 * isn't part of the job, so it has to reset on every document switch.
 */
export function MockupPanel() {
  const doc = useStore((s) => s.docs.find((d) => d.id === s.activeId));
  const docs = useStore((s) => s.docs);
  const templates = useStore((s) => s.templates);
  const templateStatus = useStore((s) => s.templateStatus);
  const scenes = useStore((s) => s.scenes);
  const mockupDefaults = useStore((s) => s.mockupDefaults);
  const setMockupJob = useStore((s) => s.setMockupJob);
  const setMockupBackdrop = useStore((s) => s.setMockupBackdrop);
  const setMockupColors = useStore((s) => s.setMockupColors);
  const setBackDoc = useStore((s) => s.setBackDoc);
  const setMockupDefaults = useStore((s) => s.setMockupDefaults);

  // Hooks run unconditionally (React's rule), even on the early-return paths
  // below where there is no job yet to draft an offset for.
  const job = doc?.mockup?.job;
  const [xDraft, setXDraft] = useState(job && job.placement.xIn !== 'center' ? String(job.placement.xIn) : '');
  const xFocused = useRef(false);
  const xDirty = useRef(false);
  useEffect(() => {
    setXDraft((prev) => nextDraft(prev, job && job.placement.xIn !== 'center' ? String(job.placement.xIn) : '', xFocused.current));
  }, [job?.placement.xIn]);

  const [yDraft, setYDraft] = useState(job && job.placement.yIn !== 'center' ? String(job.placement.yIn) : '');
  const yFocused = useRef(false);
  const yDirty = useRef(false);
  useEffect(() => {
    setYDraft((prev) => nextDraft(prev, job && job.placement.yIn !== 'center' ? String(job.placement.yIn) : '', yFocused.current));
  }, [job?.placement.yIn]);

  // The angle gets the same draft/commit treatment as the offsets: it is typed as well as
  // dragged (on the slider here, and on the viewer's rotate handle), and a half-typed "-" or "9"
  // must not reach the job on its way to "-90".
  const [rotDraft, setRotDraft] = useState(job ? String(job.rotationDeg ?? 0) : '');
  const rotFocused = useRef(false);
  const rotDirty = useRef(false);
  useEffect(() => { setRotDraft((prev) => nextDraft(prev, job ? String(job.rotationDeg ?? 0) : '', rotFocused.current)); }, [job?.rotationDeg]);

  /** The template editor's seed, or null while it is closed. One piece of state
   *  rather than a seed and a photo that could disagree: a blank editor is
   *  `{ seed: null, photo: null }`, which is still "open". */
  const [editor, setEditor] = useState<{ seed: MockupTemplate | null; photo: Blob | null } | null>(null);
  /** A failed duplicate (its photo could not be fetched) or a failed delete. */
  const [templateError, setTemplateError] = useState<string | null>(null);
  /** The user template whose Delete has been clicked once — a second click on
   *  the same one goes through. No `window.confirm`. */
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const userTemplates = templates.filter((t) => t.kind === 'user');
  const styleSlugs = [...new Set(templates.map((t) => t.template.style))];
  /** IndexedDB refused to open: whatever is authored lasts for this session. */
  const sessionOnly = templateStoreKind() === 'memory';

  /** Open the editor on a copy of `source`: a NEW user-template id (so a save
   *  can never write over the blank it was copied from) and its photo in hand —
   *  a built-in's comes off the network, a user template's out of IndexedDB. */
  const openCopy = async (source: TemplateSource) => {
    setTemplateError(null);
    try {
      let blob = source.photoBlob ?? null;
      if (!blob) {
        if (!source.photoUrl) throw new Error(`template "${source.template.id}" has no photo`);
        const res = await fetch(source.photoUrl);
        if (!res.ok) throw new Error(`${source.photoUrl}: HTTP ${res.status}`);
        blob = await res.blob();
      }
      setEditor({ seed: { ...source.template, id: newTemplateId('u'), name: `${source.template.name} copy` }, photo: blob });
    } catch (e) {
      setTemplateError((e as Error).message);
    }
  };

  const onDelete = (id: string) => {
    if (confirmDelete !== id) { setConfirmDelete(id); return; }
    setConfirmDelete(null);
    setTemplateError(null);
    void deleteUserTemplate(id).catch((e: Error) => setTemplateError(e.message));
  };

  // Rendered from EVERY branch below: with no blanks at all there is no job to
  // hang a panel off (and authoring the first template is exactly what that
  // document needs), and closing the last file must not take an open editor
  // with it. A switch to another document still does: this panel is keyed by
  // `activeId`, so it remounts — see the Task 5 report.
  const editorNode = editor && (
    <TemplateEditor
      seed={editor.seed} seedPhoto={editor.photo} styles={styleSlugs}
      onCancel={() => setEditor(null)}
      onSaved={(saved) => {
        setEditor(null);
        // Show it straight away on the document being worked on. A document
        // with no job yet is seeded on it by the session's subscription instead.
        // Through `templateChange`, like the picker: the colours and the job
        // have to stay in step whichever way the blank was chosen.
        const m = doc?.mockup;
        if (!doc || !m) return;
        const changed = templateChange(m.job, m.colors, saved, templates.find((t) => t.template.id === m.job.templateId)?.template, templates);
        // One undo step for the pair, as everywhere the colours and the job move together.
        undoGroup('Template', () => {
          if (changed.colors !== m.colors) setMockupColors(doc.id, changed.colors);
          setMockupJob(doc.id, changed.job);
        }, `template:${doc.id}`, mockupScope(doc.id));
      }}
    />
  );

  if (!doc) {
    return (
      <div className="section mockup-panel">
        <div className="muted small">Open a file to set up its mockup.</div>
        {editorNode}
      </div>
    );
  }
  if (!doc.mockup || !job) {
    return (
      <div className="section mockup-panel">
        <div className="muted small">{templates.length ? 'Preparing…' : 'No templates are loaded — add one to get started.'}</div>
        <div className="row"><button type="button" onClick={() => setEditor({ seed: null, photo: null })}>New template…</button></div>
        {templateError && <div className="error small">{templateError}</div>}
        {editorNode}
      </div>
    );
  }

  const mockup = doc.mockup;
  const currentSource = templates.find((t) => t.template.id === job.templateId);
  const template = currentSource?.template;
  const groups = groupByStyle(templates);
  const backCandidates = template ? candidatesFor(templates, template.style, 'back') : [];
  const hasBack = backCandidates.length > 0;
  // A back design has to have a completed run to composite — a document that
  // merely opened cleanly (`status === 'ready'`) but hasn't been through the
  // pipeline yet has nothing to render into the back blank.
  const backOptions = [{ value: '', label: 'None' }, ...docs.filter((d) => d.id !== doc.id && d.status === 'ready' && d.run).map((d) => ({ value: d.id, label: d.name }))];

  // A plain write: `setMockupJob` sanitises every job that reaches it — the rotation and each
  // print held to the ranges these controls offer — and does so WITHOUT a template, so a print
  // this blank has no area for survives an unrelated width or colour edit and is reported as
  // skipped rather than deleted behind the shop's back. Only the "More prints" rows below check
  // an area, and only the one they are writing to (`setExtraPrint`).
  const commitJob = (patch: Partial<typeof job>) => setMockupJob(doc.id, { ...job, ...patch });
  const commitPlacement = (patch: Parameters<typeof placementPatch>[1]) => commitJob({ placement: placementPatch(job.placement, patch) });

  /** Picking a blank by hand: the colours and the job are re-derived together, so
   *  the pressed swatch still drives the preview (`templateChange`). A template
   *  the registry no longer holds — the "Missing template" option — has no style
   *  to derive against, so it is simply recorded. */
  const onTemplateChange = (nextId: string) => {
    const next = templates.find((t) => t.template.id === nextId)?.template;
    if (!next) { commitJob({ templateId: nextId }); return; }
    const changed = templateChange(job, mockup.colors, next, template, templates);
    // The blank and the colourways it brought with it are one choice, so they are
    // one undo: putting the blank back and leaving the colours changed would be
    // a state the shop never asked for.
    undoGroup('Template', () => {
      if (changed.colors !== mockup.colors) setMockupColors(doc.id, changed.colors);
      setMockupJob(doc.id, changed.job);
    }, `template:${doc.id}`, mockupScope(doc.id));
  };

  /**
   * A colourway added, previewed or removed. Two store writes — the colour list,
   * and the job that has to wear the colour now on screen (a different blank, or
   * the same one recoloured) — collapsed into ONE undo step: half an undo would
   * put the garment back and leave the colour gone.
   *
   * `label` is what the toast and the undo say; the chip's × passes "Removed
   * Charcoal", and a swatch click (which only ever previews) takes the default.
   *
   * Only the previews COALESCE. A removal is a one-off, and keyed like a preview it
   * merges into a swatch click made in the half-second before it — keeping that
   * click's snapshot, so the toast says "Removed Charcoal — Undo" and the button
   * hands back the list from before the colour the shop had just added.
   */
  const onColorsChange = (nextColors: string[], label?: string) => {
    undoGroup(label ?? 'Garment colours', () => {
      setMockupColors(doc.id, nextColors);
      if (template) {
        const { templateId, garment } = garmentForColors(nextColors, template, templates);
        setMockupJob(doc.id, { ...job, templateId, garment });
      }
    }, label === undefined ? `colours:${doc.id}` : undefined, mockupScope(doc.id));
  };

  /** The two sticky centres, read one axis at a time — the panel's "Centre across"/"Centre down". */
  const centredAcross = job.placement.xIn === 'center';
  const centredDown = job.placement.yIn === 'center';
  /** The pixel size of the art this document's own print draws, or undefined before its first run. */
  const artSize = doc.run ? { width: doc.run.width, height: doc.run.height } : undefined;
  /** What a centred axis WORKS OUT TO in inches — `artRectIn`'s own arithmetic, so the disabled
   *  field shows the offset the render is using rather than going blank (audit #18), and un-ticking
   *  a toggle hands the numbers straight over instead of jumping the art. Rounded to the hundredth:
   *  the fields are tenths of an inch in practice, and `4.2915000000000005` is not a position. */
  const round2 = (v: number) => Math.round(v * 100) / 100;
  const artHeightIn = artSize && artSize.width > 0 ? (job.placement.widthIn * artSize.height) / artSize.width : 0;
  const centredX = template ? clamp(round2((template.printArea.widthIn - job.placement.widthIn) / 2), OFFSET_IN_MIN, OFFSET_IN_MAX) : 0;
  const centredY = template ? clamp(round2((template.printArea.heightIn - artHeightIn) / 2), OFFSET_IN_MIN, OFFSET_IN_MAX) : 0;
  const commitX = (raw: string) => {
    const current = job.placement.xIn === 'center' ? centredX : job.placement.xIn;
    const v = commitDraft(raw, current, OFFSET_IN_MIN, OFFSET_IN_MAX);
    setXDraft(String(v));
    if (centredAcross || v !== job.placement.xIn) commitPlacement({ xIn: v });
  };
  const commitY = (raw: string) => {
    const current = job.placement.yIn === 'center' ? centredY : job.placement.yIn;
    const v = commitDraft(raw, current, OFFSET_IN_MIN, OFFSET_IN_MAX);
    setYDraft(String(v));
    if (centredDown || v !== job.placement.yIn) commitPlacement({ yIn: v });
  };
  /** Does the print still fit its area? The same predicate the compositor reports as `clipped`
   *  (`printFits`), computed here so the warning under Width appears as the width is dragged rather
   *  than one render later. Without a template there is no area to measure against. */
  const fits = !template || printFits(template.printArea, job.placement, job.rotationDeg ?? 0, artSize);
  /** The primary print's own area — `printAreas[0]`, the one `job.placement` is measured in — and
   *  the art it draws. A preset and an alignment need BOTH: the area to place inside, and the art's
   *  pixel size for the one thing a width cannot give, how tall the print is. Either can be
   *  missing (a template the registry has lost, a file that has not been through the pipeline), and
   *  without them the Preset select and the Align grid are dead rather than guessing. */
  const placeArea = template?.printArea;
  const canPlace = !!placeArea && !!artSize;
  /** The preset the placement IS, which is what the select shows — "Custom" for a placement that is
   *  none of them, including every one a drag or a typed inch lands on. */
  const presetId: PresetId | 'custom' = placeArea && artSize ? matchPreset(placeArea, artSize, job.placement) : 'custom';
  const presetHint = PLACEMENT_PRESETS.find((p) => p.id === presetId)?.hint;

  const rotation = job.rotationDeg ?? 0;
  const commitRotation = (raw: string) => {
    const v = commitDraft(raw, rotation, ROTATION_MIN, ROTATION_MAX, 1);
    setRotDraft(String(v));
    if (v !== rotation) commitJob({ rotationDeg: v });
  };
  /** The blank's other areas — the ones an extra print can go in. `printAreas[0]` is the job's
   *  own print (`job.placement`), and an area with no id of its own is nothing a print can name,
   *  so neither gets a row. Empty for every blank shipped today, which is what keeps the "More
   *  prints" section invisible until a shop authors a second area. */
  const extraAreas: Array<PrintArea & { id: string }> = template
    ? areasOf(template).slice(1).filter((a): a is PrintArea & { id: string } => !!a.id)
    : [];
  /** Prints for areas this blank does NOT have — kept on purpose when the job moved here, but
   *  offered by none of the rows above, so without this list the only way to be rid of one was to
   *  go back to the blank it came from. */
  const retained = retainedPrints(job, template);
  const designOptions = extraDesignOptions(docs, doc.id);
  /** The pixel size of the art a print would draw — this document's own run for `null` (or for
   *  its own id), else the named document's. Undefined for a document that has not been through
   *  the pipeline, which is exactly when there is no size to place against. */
  const artSizeOf = (docId: string | null): { width: number; height: number } | undefined => {
    const run = !docId || docId === doc.id ? doc.run : docs.find((d) => d.id === docId)?.run;
    return run ? { width: run.width, height: run.height } : undefined;
  };

  const wrinkleValue = Math.round((job.wrinkle ?? template?.shading.wrinkle ?? defaultShadingSettings().wrinkle) * 1000);
  const textureValue = Math.round((job.texture ?? template?.shading.texture ?? defaultShadingSettings().texture) * 100);

  return (
    <div className="mockup-panel">
      <Section id="template" title="Template">
        <label className="ctl">
          <span className="ctl-label">Template</span>
          <select aria-label="Template" value={job.templateId} onChange={(e) => onTemplateChange(e.target.value)}>
            {/* `job.templateId` may point at a template no longer in the
                registry (a deleted user template, a redeploy that dropped a
                blank) — an option for it keeps the select's value matching a
                real option instead of silently showing nothing selected. */}
            {!template && <option value={job.templateId} disabled>Missing template</option>}
            {templateOptions(groups).map((g) => (
              <optgroup key={g.label} label={g.label}>
                {g.options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </optgroup>
            ))}
          </select>
        </label>
        <div className="row">
          <button type="button" onClick={() => setEditor({ seed: null, photo: null })}>New template…</button>
          <button
            type="button" disabled={!currentSource} title={currentSource ? undefined : 'Select a template first'}
            onClick={() => currentSource && void openCopy(currentSource)}
          >
            Duplicate to My templates
          </button>
        </div>
        {templateError && <div className="error small">{templateError}</div>}
      </Section>

      {(userTemplates.length > 0 || sessionOnly) && (
        <Section id="my-templates" title="My templates">
          {/* Said here as well as in the editor: this is where a shop comes back
              to a template it authored, and finding the list empty after a
              reload should not be the first news of it. */}
          {sessionOnly && <div className="muted small">{SESSION_ONLY_NOTICE}</div>}
          {userTemplates.map((s) => (
            <div key={s.template.id} className="row template-row">
              <span className="template-name" title={s.template.name}>{s.template.name}</span>
              <button type="button" className="right" onClick={() => setEditor({ seed: s.template, photo: s.photoBlob ?? null })}>Edit</button>
              <button type="button" onClick={() => onDelete(s.template.id)}>
                {confirmDelete === s.template.id ? 'Really delete?' : 'Delete'}
              </button>
            </div>
          ))}
        </Section>
      )}

      <ColorSwatches
        sources={templates} style={template?.style ?? ''} colors={mockup.colors}
        recolorable={template ? template.recolorable !== false : true}
        onChange={onColorsChange}
      />

      <Section id="design" title="Design">
        <div className="row"><span className="ctl-label">Front</span><span>{doc.name}</span></div>
        <label className="ctl">
          <span className="ctl-label">Back design</span>
          <select aria-label="Back design" value={mockup.backDocId ?? ''} disabled={!hasBack} onChange={(e) => setBackDoc(doc.id, e.target.value || null)}>
            {backOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </label>
        {/* Audit #10: this used to tell a customer to drop a file into a folder and
            run an npm script. What a shop can actually do from here is author the
            blank in the editor, so that is what it says — and nothing else. */}
        {!hasBack && (
          <div className="muted small">This style has no back photo yet. Add one with New template… (view: Back).</div>
        )}
        {/* The back preview is rendered from a job of this document's (`backJob`)
            on the style's back blank, so a failure there belongs here rather
            than on the bound document's own panel, which knows nothing of it. */}
        {mockup.backError && <div className="error small">Back preview: {mockup.backError}</div>}
      </Section>

      <Section id="placement" title="Placement">
        {/* The placements a shop asks for by name, first in the section: one press sets the width
            and both offsets at once, and everything below it is the same numbers to nudge
            afterwards. It writes through `commitPlacement` — the very write the fields use — so a
            preset is one `Ctrl+Z` ("Placement") and re-renders like any other placement edit.
            "Left chest" is the WEARER's left, the right half as you look at a front view; the label
            and the hint both say so, because it is the thing got wrong most often. */}
        <label className="ctl ctl-wide">
          <span className="ctl-label">Preset</span>
          <select
            aria-label="Placement preset" title="One-press placements" value={presetId} disabled={!canPlace}
            onChange={(e) => {
              const picked = e.target.value;
              // "Custom" is what the select READS for a placement that is no preset; choosing it
              // is not an instruction to move anything.
              if (picked === 'custom' || !placeArea || !artSize) return;
              commitPlacement(applyPreset(picked as PresetId, placeArea, artSize));
            }}
          >
            <option value="custom">Custom</option>
            {PLACEMENT_PRESETS.map((p) => <option key={p.id} value={p.id} title={p.hint}>{p.label}</option>)}
          </select>
        </label>
        {/* The chosen preset explained in one line, because a `title` on an <option> is not
            something a shop finds. Nothing for "Custom": it describes no placement. */}
        {presetHint && <div className="muted small">{presetHint}</div>}
        {!canPlace && <div className="muted small">no design yet — a preset and Align need the print's own size</div>}
        <Slider label="Width" value={job.placement.widthIn} min={WIDTH_IN_MIN} max={WIDTH_IN_MAX} step={0.1} unit="in" onChange={(v) => commitPlacement({ widthIn: v })} />
        {/* Audit #8: an over-size print used to be reported by one grey word ("clipped") on the
            viewer, well away from the control that caused it. It is said HERE, under Width, in
            plain words and the warn colour — and `role="status"` so a screen reader hears it
            appear without the focus moving. The footer says "Clipped" for the same job. */}
        {!fits && <div className="small warn-text" role="status">Wider than the area — the print will be cut off</div>}
        {/* Across, then Down: one sticky toggle and one number per axis, in that order, so
            "centre it" and "and this far in" read as the same row (audit #5). While an axis is
            centred its field is DISABLED but shows the inch value the render is using, because a
            blank, dead field explains nothing about where the art went (audit #18). */}
        <div className="ctl">
          <span className="ctl-label">Across</span>
          <Toggle label="Centre across" checked={centredAcross} onChange={(v) => commitPlacement({ xIn: v ? 'center' : centredX })} />
          <input
            type="number" className="ctl-num" aria-label="X offset" title="Distance from the left edge of the print area"
            min={OFFSET_IN_MIN} max={OFFSET_IN_MAX} step={0.1} disabled={centredAcross}
            value={centredAcross ? String(centredX) : xDraft}
            onFocus={() => { xFocused.current = true; }}
            onChange={(e) => { setXDraft(e.target.value); xDirty.current = true; }}
            onBlur={() => { xFocused.current = false; if (xDirty.current) { commitX(xDraft); xDirty.current = false; } }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { commitX(xDraft); xDirty.current = false; }
              else if (e.key === 'Escape') { setXDraft(centredAcross ? '' : String(job.placement.xIn)); xDirty.current = false; }
            }}
          />
          <span className="muted small">in</span>
        </div>
        <div className="ctl">
          <span className="ctl-label">Down</span>
          <Toggle label="Centre down" checked={centredDown} onChange={(v) => commitPlacement({ yIn: v ? 'center' : centredY })} />
          <input
            type="number" className="ctl-num" aria-label="Y offset" title="Distance from the top edge of the print area"
            min={OFFSET_IN_MIN} max={OFFSET_IN_MAX} step={0.1} disabled={centredDown}
            value={centredDown ? String(centredY) : yDraft}
            onFocus={() => { yFocused.current = true; }}
            onChange={(e) => { setYDraft(e.target.value); yDirty.current = true; }}
            onBlur={() => { yFocused.current = false; if (yDirty.current) { commitY(yDraft); yDirty.current = false; } }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { commitY(yDraft); yDirty.current = false; }
              else if (e.key === 'Escape') { setYDraft(centredDown ? '' : String(job.placement.yIn)); yDirty.current = false; }
            }}
          />
          <span className="muted small">in</span>
        </div>
        {/* Nine positions in the area, after the two axes they are a shortcut for: the middle column
            and the middle row write the sticky `'center'`, so pressing the grid's own middle is the
            "Centre in area" button below and ticks both toggles above. The width is never touched —
            aligning is a move. */}
        <div className="ctl">
          <span className="ctl-label">Align</span>
          <AlignGrid
            value={placeArea && artSize ? currentAnchor(placeArea, artSize, job.placement) : null}
            disabled={!canPlace}
            onPick={(a) => { if (placeArea && artSize) commitPlacement(alignPlacement(a, placeArea, artSize, job.placement)); }}
          />
        </div>
        {/* The one-press version of the two toggles above, for the commonest placement a shop does:
            dead centre of the area. Secondary, not primary — the panel's one primary button is the
            mode's export. */}
        <div className="row">
          <button type="button" className="btn-secondary" onClick={() => commitPlacement({ xIn: 'center', yIn: 'center' })}>Centre in area</button>
        </div>
        <label className="ctl">
          <span className="ctl-label">Rotation</span>
          {/* ONE control, one range. The slider used to span ±45 while the field beside it spanned
              ±180, so a typed 180 left the thumb parked at 45 and the next arrow press rewrote the
              angle as 44 (audit #3). Both now read the same value over the same −180…180; the
              field still takes a decimal, and the slider shows it rounded to the whole degree it
              steps in. The viewer's rotate handle writes the same field. */}
          <input
            type="range" aria-label="Rotation" min={ROTATION_MIN} max={ROTATION_MAX} step={1}
            value={clamp(Math.round(rotation), ROTATION_MIN, ROTATION_MAX)}
            onChange={(e) => commitJob({ rotationDeg: Number(e.target.value) })}
          />
          <input
            type="number" className="ctl-num" aria-label="Rotation" min={ROTATION_MIN} max={ROTATION_MAX} step={1}
            value={rotDraft}
            onFocus={() => { rotFocused.current = true; }}
            onChange={(e) => { setRotDraft(e.target.value); rotDirty.current = true; }}
            onBlur={() => { rotFocused.current = false; if (rotDirty.current) { commitRotation(rotDraft); rotDirty.current = false; } }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') { commitRotation(rotDraft); rotDirty.current = false; }
              else if (e.key === 'Escape') { setRotDraft(String(rotation)); rotDirty.current = false; }
            }}
          />
          <span className="muted small">°</span>
        </label>
        {/* Only with something to reset: an "undo" that does nothing is one more control to read
            past on a panel that is already long. */}
        {rotation !== 0 && <div className="row"><button type="button" onClick={() => commitJob({ rotationDeg: 0 })}>Reset rotation</button></div>}
        {doc.run && template && <div className="muted small">{artSizeLine(doc.run, job.placement, template)}</div>}
      </Section>

      {/* Only for a blank that HAS other areas. Every template shipped today carries a chest
          alone, so this section stays invisible until a shop authors a second area. */}
      {extraAreas.length > 0 && (
        <Section id="more-prints" title="More prints">
          {extraAreas.map((area) => {
            const name = area.name ?? titleCaseId(area.id);
            const print = extraPrintFor(job, area.id);
            const setPrint = (patch: ExtraPrintPatch | null) => setMockupJob(doc.id, setExtraPrint(job, area.id, patch, template));
            /** The art THIS row's print draws, which is what its own Align grid measures against —
             *  the row's design, not the document's, and undefined for one whose file is closed or
             *  has never been run. */
            const rowArt = print ? artSizeOf(print.docId) : undefined;
            return (
              <div key={area.id} className="extra-print">
                <label className="ctl">
                  <span className="ctl-label">{name}</span>
                  {/* "None" removes the print outright rather than disabling it: the Enabled
                      toggle below is for keeping a print's placement while leaving it out of one
                      render, which is a different intention. */}
                  <select
                    aria-label={`Design for ${name}`}
                    // This document's own id reads as "This file": the compositor treats
                    // `docId: null` and this document's id as the same print (`extraArtsFor`), so
                    // the select must not fall through to "None" for a job that names it.
                    value={print ? (print.docId && print.docId !== doc.id ? print.docId : 'self') : ''}
                    onChange={(e) => {
                      const picked = e.target.value;
                      if (!picked) { setPrint(null); return; }
                      const docId = picked === 'self' ? null : picked;
                      // A NEW print is placed against the art it will draw, so it starts inside
                      // the area whatever shape that design is; an existing one keeps its own
                      // placement — changing the design is not a reason to move it.
                      setPrint(print ? { docId } : { docId, placement: defaultExtraPlacement(area, artSizeOf(docId)) });
                    }}
                  >
                    {/* A print whose document has been CLOSED still names it: without an option
                        for that id the select would read "None" over a print that exists — the
                        same reason the Template select carries a "Missing template" entry. The
                        render reports the area as skipped and the inspector says "no design". */}
                    {print?.docId && print.docId !== doc.id && !designOptions.some((o) => o.value === print.docId) && (
                      <option value={print.docId} disabled>Missing design</option>
                    )}
                    {designOptions.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
                {print && (
                  <>
                    {/* The AREA's own width is the ceiling, not the panel's 40 in: a 4 in sleeve
                        cannot hold a 12 in print, and the compositor would only clip it. */}
                    <Slider
                      label={`Width (${name})`} value={print.placement.widthIn} min={WIDTH_IN_MIN} max={Math.max(WIDTH_IN_MIN, area.widthIn)} step={0.1} unit="in"
                      onChange={(v) => setPrint({ placement: { widthIn: v } })}
                    />
                    <Slider
                      label={`X offset (${name})`} value={print.placement.xIn === 'center' ? 0 : print.placement.xIn} min={OFFSET_IN_MIN} max={OFFSET_IN_MAX} step={0.1} unit="in"
                      onChange={(v) => setPrint({ placement: { xIn: v } })}
                    />
                    <Slider
                      label={`Y offset (${name})`} value={print.placement.yIn === 'center' ? 0 : print.placement.yIn} min={OFFSET_IN_MIN} max={OFFSET_IN_MAX} step={0.1} unit="in"
                      onChange={(v) => setPrint({ placement: { yIn: v } })}
                    />
                    {/* The same nine positions as the primary print's, in THIS area and against THIS
                        row's own art. The group names the print it belongs to, so a blank with
                        several areas does not offer several identically-announced grids.
                        `sticky: false`: this row has no Centre toggle and its X/Y are plain
                        sliders, which would read the primary print's `'center'` as 0 and throw a
                        centred print at the edge on the next nudge — so the middle column and row
                        write the NUMBER they work out to, exactly as `defaultExtraPlacement` does. */}
                    <div className="ctl">
                      <span className="ctl-label">{`Align (${name})`}</span>
                      <AlignGrid
                        label={`Align (${name})`}
                        value={rowArt ? currentAnchor(area, rowArt, print.placement, { sticky: false }) : null}
                        disabled={!rowArt}
                        onPick={(a) => { if (rowArt) setPrint({ placement: alignPlacement(a, area, rowArt, print.placement, { sticky: false }) }); }}
                      />
                    </div>
                    {!rowArt && <div className="muted small">no design — Align needs this print's own size</div>}
                    {/* The full ±180 on one control here: a sleeve print is usually a quarter
                        turn, so the range the primary print types is the range this one drags. */}
                    <Slider
                      label={`Rotation (${name})`} value={print.rotationDeg} min={ROTATION_MIN} max={ROTATION_MAX} step={1} unit="°"
                      onChange={(v) => setPrint({ rotationDeg: v })}
                    />
                    <Toggle label={`Enabled (${name})`} checked={print.enabled} onChange={(v) => setPrint({ enabled: v })} />
                  </>
                )}
              </div>
            );
          })}
        </Section>
      )}

      {/* Prints for areas THIS blank does not have. They are kept deliberately — a job moved to a
          blank with no sleeve keeps its sleeve print rather than losing the design the shop chose —
          but the rows above only offer the areas the blank HAS, so before this list the only way to
          drop one was to switch back to the blank it came from. Under "More prints", or in its place
          on a one-area blank (where that section is not rendered at all). */}
      {retained.length > 0 && (
        <Section id="retained" title="Retained prints">
          <div className="muted small">Not printed on this blank — it has no such area. The render reports them as skipped.</div>
          {retained.map((r) => (
            <div key={r.areaId} className="extra-print row">
              <span className="small" style={{ flex: 1, minWidth: 0 }}>{r.name} — kept from another blank</span>
              {/* Written straight (`withoutPrint`), not through `setExtraPrint`: that one refuses to
                  touch an area the blank does not offer, which is every area in this list. */}
              <button type="button" aria-label={`Remove ${r.name} print`} onClick={() => setMockupJob(doc.id, withoutPrint(job, r.areaId))}>Remove</button>
            </div>
          ))}
        </Section>
      )}

      {/* "Printed as", not "Ink look": the words used to be the top bar's own
          ("Screen print | DTF"), which choose which TOOL the app is, so two
          identical pairs of buttons meant two different things (audit #4). These
          name the print itself — screen-print ink, or a DTF transfer. */}
      <Section id="ink-look" title="Printed as">
        <div className="row" role="group" aria-label="Printed as">
          {(Object.keys(LOOK_LABELS) as InkLook[]).map((look) => (
            <button
              key={look} type="button" className={job.look === look ? 'primary' : ''} aria-pressed={job.look === look}
              onClick={() => commitJob({ look })}
            >
              {LOOK_LABELS[look]}
            </button>
          ))}
        </div>
      </Section>

      {/* After the ink look and before the realism sliders: the look is about the print, the
          backdrop about everything around the garment, and the realism sliders are the fabric
          itself. `BackdropControls` owns its own section heading. */}
      <BackdropControls
        backdrop={job.backdrop ?? null}
        scenes={scenes}
        // `prepared` is absent while a blank is still preparing — see `BackdropControls`, which
        // leaves the controls live for that window rather than flickering them disabled.
        separable={templateStatus[job.templateId]?.prepared?.separable}
        // The one field, merged into the LIVE job inside the store: a scene import takes hundreds of
        // ms (file read, worker decode, IndexedDB write) and a drag or a colour change made in that
        // window must not be undone by a job this render closed over landing on top of it.
        onChange={(backdrop) => setMockupBackdrop(doc.id, backdrop)}
      />

      <Section id="realism" title="Realism">
        <Slider label="Wrinkle" hint="How much the fabric's folds bend the print" value={wrinkleValue} min={0} max={30} step={1} onChange={(v) => commitJob({ wrinkle: v / 1000 })} />
        <Slider label="Texture" hint="How much fabric grain shows through" value={textureValue} min={0} max={100} step={1} unit="%" onChange={(v) => commitJob({ texture: v / 100 })} />
        {/* Named for what it puts back, not for where it reads it from (audit #22). */}
        <button type="button" onClick={() => commitJob({ wrinkle: null, texture: null })}>Reset wrinkle &amp; texture</button>
      </Section>

      <Section id="export" title="Export">
        <Select
          label="Format" value={mockupDefaults.format} options={FORMAT_OPTIONS}
          onChange={(v) => setMockupDefaults({ format: v as MockupFormat })}
        />
        <Slider
          label="Width of each picture" hint="Width of each picture in the sheet" value={mockupDefaults.sheet.tileW} min={TILE_MIN} max={TILE_MAX} step={10} unit="px"
          onChange={(v) => setMockupDefaults({ sheet: { ...mockupDefaults.sheet, tileW: v } })}
        />
        {/* The caption bar under each tile of a colourway sheet or a front+back
            pair — the colour's name (or hex), and "Front"/"Back". Off writes the
            tiles bare, for a shop that captions its own presentations. */}
        <Toggle
          label="Colour name under each picture" checked={mockupDefaults.sheet.label}
          onChange={(v) => setMockupDefaults({ sheet: { ...mockupDefaults.sheet, label: v } })}
        />
      </Section>

      {/* The render status is in the panel's pinned footer now (`PanelFooter`),
          with the same `.mockup-status` element, classes and `data-*`
          attributes: "did it render, and is it clipped" is what a shop checks
          after every edit, and it used to be below the fold. */}

      {editorNode}
    </div>
  );
}
